import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { Link } from 'react-router';
import browserHistory from '../../util/browserHistory';
import { createStyleSheet } from 'jss-theme-reactor';
import classNames from 'classnames';
import url from 'url';

import AppStructure from '../../AppStructure/AppStructure';
import Notifications from '@catalytic/pushbot-ui/SvgIcon/Notifications';
import NavTasks from '@catalytic/pushbot-ui/SvgIcon/Nav/NavTasks';
import NavProcesses from '@catalytic/pushbot-ui/SvgIcon/Nav/NavProcesses';
import NavData from '@catalytic/pushbot-ui/SvgIcon/Nav/NavData';
import NavDataMagnetic from '@catalytic/pushbot-ui/SvgIcon/Nav/NavDataMagnetic';
import NavStartAProcess from '@catalytic/pushbot-ui/SvgIcon/Nav/NavStartAProcess';
import NavTemplates from '@catalytic/pushbot-ui/SvgIcon/Nav/NavTemplates';
import NavIntegrations from '@catalytic/pushbot-ui/SvgIcon/Nav/NavIntegrations';
import NavTriggers from '@catalytic/pushbot-ui/SvgIcon/Nav/NavTriggers';
import Member from '@catalytic/pushbot-ui/SvgIcon/Member';
import NavTeam from '@catalytic/pushbot-ui/SvgIcon/Nav/NavTeam';
import NavHelp from '@catalytic/pushbot-ui/SvgIcon/Nav/NavHelp';
import NavSignOff from '@catalytic/pushbot-ui/SvgIcon/Nav/NavSignOff';
import Badge from '@catalytic/pushbot-ui/Badge';
import IconButton from '@catalytic/pushbot-ui/IconButton';
import {
  ListItem,
  ListItemIcon,
  ListItemText
} from '@catalytic/pushbot-ui/List';
import { MenuItem } from '@catalytic/pushbot-ui/Menu';
import { UserAvatar } from '@catalytic/pushbot-ui/Avatar';

import fonts from '@catalytic/pushbot-ui-xtras/fonts';

import FilterListContainer from '../../Search/FilterListContainer';
import UserWatchListContainer from '../../UserWatchList/UserWatchListContainer';

import featurePropTypes from '../../types/feature';

import { content } from '../../util/ContentTypes';
import context from '../../util/context';
import { formatSearch } from '../../util/url';
import { getUserThumbnailAltText } from '../../util/users';

export const SEARCH_SCHEMA = {
  keywords: [
    'assignedTo',
    'category',
    'editableBy',
    'group',
    'isArchived',
    'manuallyStartable',
    'order',
    'owner',
    'pageKey',
    'participatingUsers',
    'processID',
    'sort',
    'status',
    'tableType',
    'viewableBy'
  ]
};

export const styleSheet = createStyleSheet('xApp', theme => {
  return {
    '@global': {
      ...fonts({ dirname: '/' })
    },
    root: theme.typography.body1,
    signOutLink: {
      color: theme.palette.text.secondary
    },
    [theme.breakpoints.up('md')]: {
      hideAtMedium: theme.mixins.hidden()
    }
  };
});

let handleEvent;

@content({
  meta: {
    build: PropTypes.string,
    data: PropTypes.string,
    desktopProfile: PropTypes.string,
    desktopSignOut: PropTypes.string,
    desktopTeam: PropTypes.string,
    integrations: PropTypes.string,
    mobileProfile: PropTypes.string,
    mobileSignOut: PropTypes.string,
    mobileTeam: PropTypes.string,
    models: PropTypes.string,
    notifications: PropTypes.string,
    pushbots: PropTypes.string,
    start: PropTypes.string,
    tasks: PropTypes.string,
    triggers: PropTypes.string
  }
})
export default class App extends PureComponent {
  static displayName = 'App';

  static propTypes = {
    /**
     * Boolean indicating whether AI Model feature flag is enabled
     */
    aiModelEnabled: PropTypes.bool,
    /**
     * If true the App Bar is displayed
     */
    appBar: PropTypes.bool,
    /**
     * The secondary component that should show up on the right side of the App Bar
     */
    appBarSecondaryComponent: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node
    ]),
    /**
     * If true the App Drawer is displayed
     */
    appDrawer: PropTypes.bool,
    /**
     * Can be any node or number of nodes.
     */
    appDrawerChildren: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]),
    /**
     * Name of application
     */
    appName: PropTypes.string,
    /**
     * Can be any node or number of nodes.
     */
    children: PropTypes.node.isRequired,
    dialog: PropTypes.func,
    /**
     * Display text used in content body
     */
    display: PropTypes.string,
    fullName: PropTypes.string,
    /**
     * Headline text used in content body
     */
    headline: PropTypes.string,
    isTeamAdmin: PropTypes.bool,
    isTeamOwner: PropTypes.bool,
    location: PropTypes.shape({
      action: PropTypes.string,
      pathname: PropTypes.string,
      query: PropTypes.shape({
        q: PropTypes.string
      })
    }),
    navigate: PropTypes.bool,
    /**
     * Called when component will mount
     */
    onDidMount: PropTypes.func.isRequired,
    /**
     * Called when application is navigated
     */
    onNavigation: PropTypes.func,
    /**
     * Called when sign out is clicked
     */
    onSignOut: PropTypes.func,
    /**
     * Search value to be displayed in the App Bar expanded search
     */
    search: PropTypes.shape({
      text: PropTypes.text
    }),
    /**
     * Presence of queryText from url determines bool passed to searchBack
     */
    queryText: PropTypes.string,
    /**
     * Whether pages should use a split-pane layout where supported
     */
    splitPaneEnabled: PropTypes.bool,
    store: PropTypes.func,
    teamName: PropTypes.string,
    /**
     * Title to be displayed in the App Bar
     */
    title: PropTypes.string,
    unreadNotificationsList: PropTypes.arrayOf(PropTypes.string),
    update: PropTypes.func,
    username: PropTypes.string,
    userThumbnail: PropTypes.string,
    watchListEnabled: featurePropTypes,
    workDoneBetterNavigationEnabled: featurePropTypes
  };

  static defaultProps = {
    onDidMount() {},
    onSearchReset() {},
    onSearchSubmit() {},
    homeLink: '/tasks'
  };

  static childContextTypes = {
    /**
     * Application context
     */
    page: PropTypes.object.isRequired
  };

  static contextTypes = {
    styleManager: PropTypes.object.isRequired
  };

  static createDefaultContext({ page: _page, update, store }) {
    const page = _page || { update, store };
    return { page };
  }

  getChildContext() {
    const { page } = this;
    return { page };
  }

  state = {
    lastQ: null
  };

  componentWillMount() {
    const { store: storeProp, update: updateProp } = this.props;
    const store = (payload, merge, path = this.getCurrentPath()) =>
      storeProp(payload, merge, path);
    const update = (payload, merge, path = this.getCurrentPath()) =>
      updateProp(payload, merge, path);

    const { page } = App.createDefaultContext({ ...this.props, store, update });
    this.page = page;

    this.page.update({ searchSchema: SEARCH_SCHEMA });
  }

  componentDidMount() {
    const { onDidMount } = this.props;
    onDidMount(this.props);

    this.addEventForChild(
      context.document,
      'click',
      'a[href]',
      (event, childElement) => {
        if (childElement) {
          const href = childElement.getAttribute('href');
          const { hostname, pathname } = url.parse(href);
          const modifier =
            event.altKey || event.ctrlKey || event.shiftKey || event.metaKey;

          if (hostname || !pathname || modifier) return;

          event.preventDefault();

          switch (childElement.getAttribute('target')) {
            case '_blank':
              return context.open(href, '_blank');
            case '_self':
            default:
              return browserHistory.push(href);
          }
        }
      }
    );
  }

  componentWillUpdate(nextProps) {
    if (this.page && nextProps.page && nextProps.page !== this.page) {
      this.page = nextProps.page;
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      location: { pathname },
      navigate,
      onNavigation
    } = this.props;
    const {
      location: { pathname: nextPathname, action }
    } = nextProps;
    if (!navigate && nextPathname !== pathname && action === 'PUSH')
      onNavigation();
  }

  componentWillUnmount() {
    this.removeEventForChild(context.document, 'click', handleEvent);
  }

  addEventForChild = (parent, eventName, childSelector, callback) => {
    handleEvent = event => {
      if (event && event.target && event.target.closest && childSelector) {
        return callback(event, event.target.closest(childSelector));
      }
    };
    parent.addEventListener(eventName, handleEvent);
  };

  removeEventForChild = (parent, eventName, handler) =>
    parent.removeEventListener(eventName, handler);

  handleSignOut = () => {
    const { teamName, username, onSignOut } = this.props;
    if (onSignOut) onSignOut({ teamName, username });
  };

  getCurrentPath = () => {
    const {
      location: { pathname }
    } = this.props;
    return pathname;
  };

  handleSearchChange = e => {
    const text = e.target.value;
    const { search } = this.props;
    const lastQueryText = (search || {}).text || '';
    if (text !== lastQueryText) {
      this.onSearchChange({ ...search, text });
    }
  };

  handleSearchExpand = (e, expandSearch) => {
    this.onSearchExpand({ expandSearch });
    if (expandSearch) this.handleSearchReset(e);
  };

  handleSearchReset = (e, props) => {
    const {
      search,
      location: {
        search: omitSearchString,
        query: { q, ...query },
        ...location
      } = {},
      onSearchReset
    } =
      props || this.props;
    const { lastQ } = this.state;
    const nextSearch = { ...search, text: '' };
    const nextLocation = { ...location, query: { ...query, q: lastQ } };

    this.onSearchChange(nextSearch);
    onSearchReset(lastQ, nextSearch, nextLocation);
    this.setState({ lastQ: null });
  };

  handleSearchSubmit = (e, props) => {
    const {
      search,
      location: {
        search: omitSearchString,
        query: { q, ...query },
        ...location
      } = {},
      onSearchSubmit
    } =
      props || this.props;
    const { lastQ } = this.state;
    const nextSearch = { text: ((search || {}).text || '').trim() };
    const nextQ = formatSearch(nextSearch, false);
    const nextLocation = { ...location, query: { ...query, q: nextQ } };

    this.onSearchChange(nextSearch);
    onSearchSubmit(nextQ, search, nextLocation);
    if (lastQ === null) {
      this.setState({ lastQ: q });
    }
  };

  onSearchChange = search => {
    const { text = '' } = search;
    const isDirty = typeof text === 'string' && text.length !== 0;
    this.page.update({ expandSearch: isDirty, search }, true);
  };

  onSearchExpand = ({ expandSearch }) => {
    this.page.update({ expandSearch: !expandSearch }, true);
  };

  isActive = pathname =>
    browserHistory.getCurrentLocation().pathname.startsWith(pathname);

  page = undefined;

  renderAppDrawerChildren = () => {
    const {
      aiModelEnabled,
      isTeamAdmin,
      isTeamOwner,
      watchListEnabled,
      workDoneBetterNavigationEnabled
    } = this.props;
    const contentTypes = this.constructor.contentTypes;
    const isAdmin = isTeamAdmin || isTeamOwner;
    let classes = {};

    if (
      this.context.styleManager.sheetMap.find(
        sheet => sheet.name === 'AppStructure'
      )
    ) {
      classes = this.context.styleManager.render(styleSheet);
    }
    /* eslint-disable */
    return [
      <ListItem
        href="/tasks"
        selected={this.isActive('/tasks')}
        {...contentTypes.props.tasks()}
      >
        <ListItemIcon>
          <NavTasks />
        </ListItemIcon>
        <ListItemText
          inherit
          noWrap
          primary="Tasks"
          selected={this.isActive('/tasks')}
        />
      </ListItem>,
      <ListItem
        href="/pushbots"
        selected={this.isActive('/pushbots')}
        {...contentTypes.props.pushbots()}
      >
        <ListItemIcon>
          <NavProcesses />
        </ListItemIcon>
        <ListItemText
          inherit
          noWrap
          primary="Processes"
          selected={this.isActive('/pushbots')}
        />
      </ListItem>,
      <ListItem
        href="/data"
        margin={!aiModelEnabled}
        selected={this.isActive('/data')}
        {...contentTypes.props.data()}
      >
        <ListItemIcon>
          <NavDataMagnetic />
        </ListItemIcon>
        <ListItemText
          inherit
          noWrap
          primary="Data"
          selected={this.isActive('/data')}
        />
      </ListItem>,
      ...(aiModelEnabled
        ? [
            <ListItem
              href="/models"
              margin
              selected={this.isActive('/models')}
              {...contentTypes.props.models()}
            >
              <ListItemIcon>
                <NavData />
              </ListItemIcon>
              <ListItemText
                inherit
                noWrap
                primary="Predictive Models"
                selected={this.isActive('/models')}
              />
            </ListItem>
          ]
        : []),
      <ListItem
        href="/start"
        margin
        selected={this.isActive('/start')}
        {...contentTypes.props.start()}
      >
        <ListItemIcon>
          <NavStartAProcess />
        </ListItemIcon>
        <ListItemText
          inherit
          noWrap
          primary="Start a Process"
          selected={this.isActive('/start')}
        />
      </ListItem>,
      <ListItem
        href="/build"
        selected={this.isActive('/build')}
        {...contentTypes.props.build()}
      >
        <ListItemIcon>
          <NavTemplates />
        </ListItemIcon>
        <ListItemText
          inherit
          noWrap
          primary="Process Templates"
          selected={this.isActive('/build')}
        />
      </ListItem>,
      ...(isAdmin
        ? [
            <ListItem
              href="/integrations"
              selected={this.isActive('/integrations')}
              {...contentTypes.props.integrations()}
            >
              <ListItemIcon>
                <NavIntegrations />
              </ListItemIcon>
              <ListItemText
                inherit
                noWrap
                primary="Integrations"
                selected={this.isActive('/integrations')}
              />
            </ListItem>
          ]
        : []),
      <ListItem
        href="/triggers"
        margin
        selected={this.isActive('/triggers')}
        {...contentTypes.props.triggers()}
      >
        <ListItemIcon>
          <NavTriggers />
        </ListItemIcon>
        <ListItemText
          inherit
          noWrap
          primary="Triggers"
          selected={this.isActive('/triggers')}
        />
      </ListItem>,
      <ListItem
        href="https://help.catalytic.com/hc/en-us"
        margin
        target="_blank"
      >
        <ListItemIcon>
          <NavHelp />
        </ListItemIcon>
        <ListItemText inherit noWrap primary="Help & Support" />
      </ListItem>,
      <ListItem
        className={classes.hideAtMedium}
        href="/profile/me"
        selected={this.isActive('/profile/me')}
        {...contentTypes.props.mobileProfile()}
      >
        <ListItemIcon>
          <Member />
        </ListItemIcon>
        <ListItemText
          inherit
          noWrap
          primary="My profile"
          selected={this.isActive('/profile/me')}
        />
      </ListItem>,
      <ListItem
        className={classes.hideAtMedium}
        href="/team"
        margin
        selected={this.isActive('/team')}
        {...contentTypes.props.mobileTeam()}
      >
        <ListItemIcon>
          <NavTeam />
        </ListItemIcon>
        <ListItemText
          inherit
          noWrap
          primary="My team"
          selected={this.isActive('/team')}
        />
      </ListItem>,
      <ListItem
        className={classNames(classes.signOutLink, classes.hideAtMedium)}
        margin
        onClick={this.handleSignOut}
        {...contentTypes.props.mobileSignOut()}
      >
        <ListItemIcon>
          <NavSignOff />
        </ListItemIcon>
        <ListItemText
          inherit
          noWrap
          primary="Sign off"
          primaryClassName={classes.signOutLink}
        />
      </ListItem>,
      ...(watchListEnabled ? [<UserWatchListContainer />] : []),
      ...(workDoneBetterNavigationEnabled ? [<FilterListContainer />] : [])
    ].map((ele, i) => React.cloneElement(ele, { key: i }));
  };
  /* eslint-enable */

  render() {
    const {
      aiModelEnabled,
      appBarSecondaryComponent,
      appName,
      children,
      dialog: dialogProp,
      fullName,
      isTeamAdmin,
      isTeamOwner,
      queryText,
      search: searchProp,
      splitPaneEnabled,
      teamName,
      title,
      unreadNotificationsList,
      username = '',
      userThumbnail,
      watchListEnabled,
      workDoneBetterNavigationEnabled,
      ...other
    } = this.props;
    const contentTypes = this.constructor.contentTypes;
    const isAdmin = isTeamAdmin || isTeamOwner; // eslint-disable-line no-unused-vars
    let classes = {};

    if (
      this.context.styleManager.sheetMap.find(
        sheet => sheet.name === 'AppStructure'
      )
    ) {
      classes = this.context.styleManager.render(styleSheet);
    }

    const { text: search = '' } = searchProp || {};
    const dialog =
      typeof dialogProp === 'function'
        ? dialogProp(this.props, this.context)
        : dialogProp;
    const userThumbnailAltText = getUserThumbnailAltText({
      fullName,
      username
    });

    return (
      <AppStructure
        {...{ ...other, appName, dialog, search, title }}
        onSearchExpand={this.handleSearchExpand}
        onSearchChange={this.handleSearchChange}
        onSearchReset={this.handleSearchReset}
        onSearchSubmit={this.handleSearchSubmit}
        appDrawer
        searchBack={!!queryText}
        appDrawerChildren={this.renderAppDrawerChildren()}
        appBarUserAvatar={
          /* eslint-disable */
          <UserAvatar
            id="userAvatar"
            src={userThumbnail}
            menuItems={[
              <MenuItem
                href="/profile/me"
                {...contentTypes.props.desktopProfile()}
              >
                My profile
              </MenuItem>,
              <MenuItem href="/team" {...contentTypes.props.desktopTeam()}>
                My team
              </MenuItem>,
              <MenuItem
                className={classes.signOutLink}
                onClick={this.handleSignOut}
                {...contentTypes.props.desktopSignOut()}
              >
                Sign off
              </MenuItem>
            ]}
          >
            {userThumbnailAltText}
          </UserAvatar>
          /* eslint-enable */
        }
        appBarSecondary={appBarSecondaryComponent}
        appBarNotifications={
          <IconButton
            component={Link}
            to="/notifications"
            id="notifications"
            {...contentTypes.props.notifications()}
          >
            {unreadNotificationsList ? (
              <Badge accent badgeContent={unreadNotificationsList.length}>
                <Notifications />
              </Badge>
            ) : (
              <Notifications />
            )}
          </IconButton>
        }
        prefix="pb: http://pushbot.com"
        className={classes.root}
        useCenteredContentColumn={!splitPaneEnabled}
      >
        {children}
      </AppStructure>
    );
  }
}



// WEBPACK FOOTER //
// ./js/components/x/App.js